import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Imperative map behaviors that need the Leaflet instance:
 * - fly to the selected property when it changes (zoom past the decluster
 *   threshold so the pin shows individually, not collapsed in a cluster)
 * - disable doubleClickZoom while the draw tool is active (so the closing
 *   double-click finishes the polygon instead of zooming)
 */
export default function MapController({ target, fit, fitKey, drawing, invalidateKey }: {
  target: { id: string; lat: number; lng: number } | null;
  fit: [[number, number], [number, number]] | null;
  fitKey: string;
  drawing: boolean;
  invalidateKey?: string;
}) {
  const map = useMap();

  useEffect(() => {
    if (drawing) map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [drawing, map]);

  // When the map is un-hidden (mobile Map/List toggle), Leaflet needs to be told
  // its container resized or it paints gray tiles.
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [invalidateKey, map]);

  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 15), { duration: 0.6 });
    // Fly only when the selected property changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  useEffect(() => {
    if (!fit) return;
    map.fitBounds(fit, { padding: [50, 50], maxZoom: 15, animate: true });
    // Fit once per search change (keyed on fitKey), not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  return null;
}
